import { v4 as uuidv4 } from 'uuid'

import { DocumentMapping } from '../../models/documentMapping'

/**
 * The bucket an option is grouped under. Options locked before group ids existed could only be
 * saved one overlay at a time, so each one without an id is its own group rather than sharing one.
 */
const lockGroupKeyOf = (mapping: DocumentMapping) =>
    mapping.additional_data.estimatedBaseHomeLockGroupId ?? `ungrouped-${mapping.document_chunk_id}`

const lockOrderOf = (mapping: DocumentMapping) => mapping.additional_data.estimatedBaseHomeLockOrder ?? Infinity

const byLockOrder = (a: DocumentMapping, b: DocumentMapping) => lockOrderOf(a) - lockOrderOf(b)

/**
 * The lock group id to stamp on every overlay in one Save. A batch containing an overlay already
 * locked to this base home joins that overlay's group, so re-saving keeps the row intact; anything
 * else is a new batch and gets a new id. An overlay locked before group ids existed has none to
 * join, so its batch gets a new id too -- it was saved on its own, so it shares a row with nothing.
 *
 * Batch order decides, not store order, so which group a mixed batch joins is predictable.
 */
export const resolveOverlayLockGroupId = (
    overlayChunkIds: number[],
    mappings: DocumentMapping[] | null,
    baseHomeMappingId?: number
): string => {
    const alreadyLockedHere = overlayChunkIds
        .map((chunkId) => mappings?.find((mapping) => mapping.document_chunk_id === chunkId))
        .find(
            (mapping) =>
                mapping?.additional_data.estimatedBaseHomeId !== undefined &&
                mapping.additional_data.estimatedBaseHomeId === baseHomeMappingId
        )

    return alreadyLockedHere?.additional_data.estimatedBaseHomeLockGroupId ?? uuidv4()
}

/**
 * Splits options locked to one base home into their save batches, each batch ordered by the lock
 * order within it. One group becomes one "+N" row in the plans tree.
 *
 * Groups come back oldest first. Lock orders run in save sequence across the whole base home, so
 * the lowest one in a group says when that batch was saved -- the group ids themselves are opaque.
 */
export const groupOptionsByLockGroup = (options: DocumentMapping[]): DocumentMapping[][] => {
    const groups = new Map<string, DocumentMapping[]>()

    options.forEach((option) => {
        const key = lockGroupKeyOf(option)
        const group = groups.get(key)

        if (group) {
            group.push(option)
        } else {
            groups.set(key, [option])
        }
    })

    return [...groups.values()]
        .map((group) => [...group].sort(byLockOrder))
        .sort((groupA, groupB) => lockOrderOf(groupA[0]) - lockOrderOf(groupB[0]))
}

/**
 * Whether two options belong to the same save batch. An option with no group id is only ever in a
 * group with itself.
 */
export const isInSameLockGroup = (a?: DocumentMapping | null, b?: DocumentMapping | null): boolean =>
    !!a && !!b && lockGroupKeyOf(a) === lockGroupKeyOf(b)
